import React from 'react';
import { 
  Alert, 
  AlertTitle, 
  Box, 
  Typography, 
  Chip, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Collapse,
  IconButton 
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TaskAlt as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';

// Компонент для красивого отображения ошибок из API с ErrorsList
const ErrorNotification = ({ error, open, onClose, severity = 'error' }) => {
  const [expanded, setExpanded] = React.useState(false);

  // Функция для парсинга ошибок из API
  const parseApiError = (error) => {
    // Проверяем, есть ли response.data
    if (error?.response?.data) {
      const data = error.response.data;
      
      // Случай SUCCESS: Успешные сообщения
      if (data.result === "SUCCESS") {
        return {
          type: 'success_message',
          title: 'Операция выполнена успешно',
          errors: [data.message || 'Операция завершена успешно'],
          hasMultipleErrors: false
        };
      }
      
      // Случай 1: Ошибки в формате { result: "ERROR!!!", ErrorsList: [...] }
      if (data.result === "ERROR!!!" && data.ErrorsList && Array.isArray(data.ErrorsList)) {
        // Определяем, это предупреждения или ошибки
        const hasWarnings = data.ErrorsList.some(err =>
          String(err.ErrorText || err.error || err.message || err).includes('уже есть в базе')
        );
        
        return {
          type: 'api_errors',
          title: hasWarnings ? 'Предупреждения при экспорте' : 'Ошибки при обработке запроса',
          errors: data.ErrorsList.map(err => err.ErrorText || err.error || err.message || err),
          hasMultipleErrors: data.ErrorsList.length > 1
        };
      }
      
      // Случай 2: Стандартные ошибки API с detail
      if (data.detail) {
        return {
          type: 'api_detail',
          title: 'Ошибка API',
          errors: [data.detail],
          hasMultipleErrors: false
        };
      }
      
      // Случай 3: Ошибки с полем message
      if (data.message) {
        return {
          type: 'api_message',
          title: 'Ошибка выполнения',
          errors: [data.message],
          hasMultipleErrors: false
        };
      }
      
      // Случай 4: Ошибки в формате { errors: [...] }
      if (data.errors && Array.isArray(data.errors)) {
        return {
          type: 'validation_errors',
          title: 'Ошибки валидации',
          errors: data.errors,
          hasMultipleErrors: data.errors.length > 1
        };
      }
    }
    
    // Случай 5: Простая строка ошибки
    if (typeof error === 'string') {
      return {
        type: 'simple_string',
        title: 'Ошибка',
        errors: [error],
        hasMultipleErrors: false
      };
    }
    
    // Случай 6: Объект Error со свойством message
    if (error?.message) {
      return {
        type: 'error_object',
        title: 'Системная ошибка',
        errors: [error.message],
        hasMultipleErrors: false
      };
    }
    
    // Случай 7: Неизвестный формат ошибки
    return {
      type: 'unknown',
      title: 'Неизвестная ошибка',
      errors: ['Произошла неизвестная ошибка. Обратитесь к администратору.'],
      hasMultipleErrors: false
    };
  };

  // Функция для дружественного перевода ошибок на русский
  const translateError = (errorText, isSuccess = false) => {
    if (!errorText) return { friendly: 'Неизвестная ошибка', icon: '❌', severity: 'error' };
    
    // Для успешных сообщений используем специальную иконку
    if (isSuccess) {
      return {
        friendly: errorText,
        icon: '✅',
        severity: 'success'
      };
    }
    
    const translations = {
      'ИНН уже есть в базе 1С!': {
        friendly: 'Компания уже добавлена в систему 1С',
        icon: '⚠️',
        severity: 'warning'
      },
      'ИНН уже есть в базе 1С!; Неверный ИД менеджера!': {
        friendly: 'Компания уже в системе 1С, но указан некорректный менеджер',
        icon: '⚠️',
        severity: 'warning'
      },
      'Неверный ИД менеджера!': {
        friendly: 'Выбран некорректный менеджер',
        icon: '👤',
        severity: 'error'
      },
      'Network Error': {
        friendly: 'Ошибка соединения с сервером',
        icon: '🌐',
        severity: 'error'
      },
      'timeout': {
        friendly: 'Превышено время ожидания ответа',
        icon: '⏱️',
        severity: 'warning'
      },
      'Unauthorized': {
        friendly: 'Необходима авторизация',
        icon: '🔐',
        severity: 'error'
      },
      'Forbidden': {
        friendly: 'Доступ запрещен',
        icon: '🚫',
        severity: 'error'
      },
      'Bad Request': {
        friendly: 'Некорректный запрос',
        icon: '❌',
        severity: 'error'
      },
      'Internal Server Error': {
        friendly: 'Внутренняя ошибка сервера',
        icon: '🔧',
        severity: 'error'
      },
      'Service Unavailable': {
        friendly: 'Сервис временно недоступен',
        icon: '🚧',
        severity: 'warning'
      },
      'Too Many Requests': {
        friendly: 'Слишком много запросов, попробуйте позже',
        icon: '⏳',
        severity: 'warning'
      },
      'Connection refused': {
        friendly: 'Соединение отклонено сервером',
        icon: '🔌',
        severity: 'error'
      },
      'ECONNABORTED': {
        friendly: 'Соединение прервано по таймауту',
        icon: '⏱️',
        severity: 'warning'
      }
    };
    
    // Точное совпадение
    if (translations[errorText]) {
      return translations[errorText];
    }
    
    // Частичное совпадение
    for (const [key, value] of Object.entries(translations)) {
      if (errorText.includes(key)) {
        return value;
      }
    }
    
    // Если перевод не найден, возвращаем оригинальный текст
    return {
      friendly: errorText,
      icon: '❌',
      severity: 'error'
    };
  };

  if (!open || !error) return null;

  const parsedError = parseApiError(error);
  const hasMultipleErrors = parsedError.hasMultipleErrors;

  // Определяем общую серьезность на основе всех ошибок
  const getOverallSeverity = () => {
    // Проверяем, это success сообщение?
    if (error?.response?.data?.result === "SUCCESS") return 'success';
    
    const severities = parsedError.errors.map(err => translateError(err).severity);
    if (severities.includes('error')) return 'error';
    if (severities.includes('warning')) return 'warning';
    return 'info';
  };

  const overallSeverity = severity === 'auto' ? getOverallSeverity() : severity;
  const isSuccessMessage = error?.response?.data?.result === "SUCCESS" || overallSeverity === 'success';

  // Кастомные иконки для Alert (не Сбер-стиль)
  const getAlertIcon = () => {
    switch (overallSeverity) {
      case 'success':
        return <SuccessIcon sx={{ fontSize: 28 }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 28 }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 28 }} />;
      default:
        return <InfoIcon sx={{ fontSize: 28 }} />;
    }
  };

  return (
    <Box sx={{ 
      position: 'fixed',
      top: 20,
      right: 20,
      zIndex: 10000,
      minWidth: 320,
      maxWidth: 450
    }}>
      <Alert 
        severity={overallSeverity} 
        onClose={onClose}
        icon={getAlertIcon()}
        sx={{ 
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          '& .MuiAlert-message': { width: '100%' }
        }}
      >
      <AlertTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="span">
            {parsedError.title}
          </Typography>
          {hasMultipleErrors && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
              <Chip 
                size="small" 
                label={`${parsedError.errors.length} ошибок`}
                color={overallSeverity}
                variant="outlined"
              />
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{ ml: 1 }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          )}
        </Box>
      </AlertTitle>

      {/* Отображение ошибок */}
      {hasMultipleErrors ? (
        <Box>
          {/* Первая ошибка всегда видна */}
          <Box sx={{ mb: 1 }}>
            {(() => {
              const firstError = translateError(parsedError.errors[0], isSuccessMessage);
              return (
                <Typography variant="body2">
                  {firstError.friendly}
                </Typography>
              );
            })()}
          </Box>

          {/* Остальные ошибки в выпадающем списке */}
          <Collapse in={expanded}>
            <List dense sx={{ mt: 1 }}>
              {parsedError.errors.slice(1).map((errorText, index) => {
                const translatedError = translateError(errorText, isSuccessMessage);
                return (
                  <ListItem key={index} sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <Box component="span" sx={{ fontSize: '1.1em' }}>
                        {translatedError.icon}
                      </Box>
                    </ListItemIcon>
                    <ListItemText 
                      primary={translatedError.friendly}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Collapse>

          {/* Подсказка о разворачивании */}
          {!expanded && parsedError.errors.length > 1 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {(() => {
                const n = parsedError.errors.length - 1;
                const form = n % 10 === 1 && n % 100 !== 11 ? 'ошибку'
                  : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 'ошибки'
                  : 'ошибок';
                return `Нажмите на стрелку, чтобы увидеть ещё ${n} ${form}`;
              })()}
            </Typography>
          )}
        </Box>
      ) : (
        // Одно сообщение - показываем сразу (без дублирования иконки)
        <Box>
          {(() => {
            const singleError = translateError(parsedError.errors[0], isSuccessMessage);
            return (
              <Typography variant="body2">
                {singleError.friendly}
              </Typography>
            );
          })()}
        </Box>
      )}

      {/* Дополнительная информация для разработчиков (только в dev режиме) */}
      {process.env.NODE_ENV === 'development' && (
        <Collapse in={expanded || !hasMultipleErrors}>
          <Box sx={{ mt: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Техническая информация:
            </Typography>
            <pre style={{ 
              fontSize: '0.7em', 
              margin: '4px 0 0 0', 
              whiteSpace: 'pre-wrap',
              color: 'text.secondary'
            }}>
              {JSON.stringify(error?.response?.data || error, null, 2)}
            </pre>
          </Box>
         </Collapse>
       )}
      </Alert>
    </Box>
   );
 };

export default ErrorNotification;
